// Shared geometry + animation timing for the DCE chrome (rail column + header
// row + canvas content panel). Kept in one place so the layout CSS, the
// rail collapse animation, and the canvas-pan compensation all agree — a
// mismatch between the width transition and the pan compensation would make the
// canvas contents visibly drift during the collapse/expand snap.

// The rail's square cell. The logo and every icon target share it by
// derivation, not by two constants that happen to agree — the owner asked for
// them to match, so changing one must change both.
export const LOGO_SIZE = 40;
export const RAIL_TARGET = LOGO_SIZE;

// The rail shows at least this many section icons however short the viewport is.
// Below it the overflow dots stop earning their slot: a rail of nothing but dots
// tells the reader there are sections and refuses to name one of them.
export const RAIL_MIN_VISIBLE_ICONS = 2;

export const RAIL_PADDING = 4;

export const RAIL_BORDER = 1;

const RAIL_BORDERS = RAIL_BORDER + RAIL_BORDER;

export const CHROME_GAP = 8;

export const RAIL_WIDTH = RAIL_PADDING + RAIL_TARGET + RAIL_PADDING + RAIL_BORDERS;
export const CONTENT_INSET = CHROME_GAP + RAIL_WIDTH + CHROME_GAP;

// The three floating header panes: an explicit 40px content box, 4px of padding
// on each edge and the palette's 1px border. Kept here because the panes set
// these in StudioTopNav and the canvas chrome anchors beneath them — a second
// literal in either place would drift the two apart.
// Minimum breathing room between the rail's icon cluster and the pinned Create
// button. No spacing alias lands on 36 (huge is 32, gigantic 48), so this is a
// deliberate raw value like RAIL_TARGET.
export const RAIL_CREATE_GAP = 36;

// Gap between rail icons (size.spacing.extraSmall resolves to this 4px).
export const RAIL_ICON_GAP = 4;

export const HEADER_PANE_HEIGHT = 40;
export const HEADER_PANE_PADDING_Y = 4;
export const HEADER_PANE_PADDING_X = 16;
export const HEADER_PANE_BORDER = 1;

const HEADER_PANE_PADDING_BOTH = HEADER_PANE_PADDING_Y + HEADER_PANE_PADDING_Y;
const HEADER_PANE_BORDERS = HEADER_PANE_BORDER + HEADER_PANE_BORDER;

export const HEADER_PANE_BOX = HEADER_PANE_HEIGHT + HEADER_PANE_PADDING_BOTH + HEADER_PANE_BORDERS;

// Everything that floats over the canvas — Layers, Elements, the zoom toolbar
// and the Inspector — starts one gap below the LOGO box, not the header panes.
// The logo box is the rail's square top cell and is the one thing that stays
// put when the rail collapses, so clearing it is what lets the left cluster
// slide to the screen edge without landing under the logo.
// The collapsed rail is exactly this box, which is why it matches the header
// panes; the canvas chrome anchors one gap below it.
export const LOGO_BOX = RAIL_PADDING + LOGO_SIZE + RAIL_PADDING + RAIL_BORDERS;

// Collapsing snaps the rail up and to width 0, but the brand mark stays pinned
// to the top-left corner. So the header reclaims the rail's lane VERTICALLY and
// never horizontally: it still has to begin past the logo, or it slides under it
// and the destination name is painted over. Derived from the logo rather than
// the rail, which is what keeps it correct if the rail is ever retuned.
export const COLLAPSED_HEADER_INSET = CHROME_GAP + LOGO_BOX + CHROME_GAP;
export const CANVAS_CONTENT_TOP = CHROME_GAP + LOGO_BOX + CHROME_GAP;

export const RAIL_TOP_PADDING = 16;

export const RAIL_TOOLTIP_DWELL_MS = 500;

// How long the pointer must rest on a rail icon before it may replace an
// ALREADY-OPEN flyout. With nothing open the rail still reveals on contact.
export const RAIL_DWELL_TAKEOVER_MS = 1000;

export const RAIL_DWELL_BLINK_MS = 200;

export const RAIL_DWELL_FADE_MS = RAIL_DWELL_TAKEOVER_MS - RAIL_DWELL_BLINK_MS;

// The left cluster slides between its expanded and collapsed anchors. It waits
// for the rail to finish snapping up before moving left, and moves right BEFORE
// the rail drops back down, so the two never animate over each other.
export const PANEL_SHIFT_MS = 300;
export const PANEL_SHIFT_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

// Two-phase snap timing. Collapse = snap the rail UP, then slide the content
// panel LEFT (wider). Expand reverses. The horizontal slide duration/easing is
// shared with the canvas-pan compensation rAF so contents stay pinned.
export const RAIL_SNAP_MS = 320;
export const CONTENT_SLIDE_MS = 300;
export const CONTENT_SLIDE_EASE = "cubic-bezier(.34, 1.7, .5, 1)";

export const PANEL_SURFACE_RADIUS = 8;

export const PANEL_SURFACE_BORDER = 1;

// The rail flyout never comes closer than this to the viewport's right edge.
export const FLYOUT_VIEWPORT_MARGIN = 12;
export const FLYOUT_CLOSE_GRACE_MS = 300;

// Leaving the flyout does not immediately arm the normal close. The first 12px
// past its right edge is a forgiving band — the resize handle lives on that edge,
// and reaching for it used to retract the panel being grabbed. Dwelling in the
// band still closes, it just takes three times as long, so the gesture is
// recoverable without making the close unreachable.
export const FLYOUT_CLOSE_BUFFER_PX = 12;
export const FLYOUT_CLOSE_BUFFER_MULTIPLIER = 3;
export const FLYOUT_MIN_WIDTH = 250;
export const FLYOUT_DEFAULT_WIDTH = 320;

export const FLYOUT_DIVIDER_WIDTH = 6;

// One above .floating-palette's own z-index (160, FloatingPalettePanel.vue), so
// the rail paints over the flyout rather than under its drop shadow.
export const RAIL_OVER_FLYOUT_Z = 161;

// The More pane overlays the flyout rather than sharing a row with it, so it
// has to paint above the panels (160) and below the seam tier (210).
export const MORE_PANE_Z = 205;

export const FLYOUT_EXPAND_MS = 150;
export const FLYOUT_CONTENT_FADE_MS = 50;

export const SIDEBAR_DRILL_MS = 260;
export const SIDEBAR_DRILL_EASE = "cubic-bezier(0.32, 0.72, 0, 1)";
// A view switch redraws the same list in place: fade out, swap, fade in.
export const SIDEBAR_VIEW_FADE_OUT_MS = 90;
export const SIDEBAR_VIEW_FADE_IN_MS = 140;
export const SIDEBAR_VIEW_FADE_EASE = "ease-out";

export function railLeftEdge() {
  return CHROME_GAP;
}

export function flyoutLeftEdge() {
  return CHROME_GAP + RAIL_WIDTH - PANEL_SURFACE_BORDER;
}

export function flyoutRightEdge(flyoutWidth) {
  return flyoutLeftEdge() + flyoutWidth + PANEL_SURFACE_BORDER + PANEL_SURFACE_BORDER;
}

export function isInFlyoutCloseBuffer(clientX, flyoutWidth) {
  const pastRightEdge = clientX - flyoutRightEdge(flyoutWidth);
  return pastRightEdge >= 0 && pastRightEdge <= FLYOUT_CLOSE_BUFFER_PX;
}


export function pinnedContentInset(flyoutWidth) {
  const borders = PANEL_SURFACE_BORDER + PANEL_SURFACE_BORDER;
  return flyoutLeftEdge() + flyoutWidth + borders + CHROME_GAP;
}
